import React, { useEffect, useState } from "react";
import {
  __,
  addIndex,
  difference,
  includes,
  isEmpty,
  map,
  pipe,
  propEq,
  pluck,
  filter,
} from "ramda";
import { useDispatch, useSelector } from "react-redux";
import { firstError } from "common/validation";
import {
  ACCEPT_OPT_IN,
  DECLINE_OPT_IN,
  SET_TERMS_ACCEPTED,
  VIEW_OPT_IN_TERMS,
} from "terms/termsActions";
import {
  SET_ACTIVE_STEP,
  SHOW_CANCEL_SUBMISSION_DIALOG,
  SUBMIT,
} from "submission/submissionActions";
import {
  getProjectTermsUri,
  getStepAcceptedOptIns,
  getStepDeclinedOptIns,
  getStepErrors,
  getStepFields,
  isTermsStepLoading,
  getTermsOptIns,
  getFieldOptions,
} from "common/selectors";
import * as steps from "common/steps";
import LoadingPanel from "general-components/LoadingPanel";
import OptIn from "general-components/OptIn";
import StepPanelButtonBar from "general-components/StepPanelButtonBar";
import OptInTermsDialog from "general-components/OptInTermsDialog";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import TermsDialog from "terms/components/TermsDialog";
import CheckIcon from "@material-ui/icons/Check";

import { makeStyles } from "@material-ui/core/styles";
import { useTranslation } from "react-i18next";
import { Typography } from "@material-ui/core";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 2,
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },

  sectionHeader: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },

  optInHeader: {
    marginTop: theme.spacing(2),
  },

  accepted: {
    color: theme.palette.primary.green.dark,
    fontStyle: "italic",
    marginTop: theme.spacing(1),
    display: "flex",
    alignItems: "center",
  },
}));

const getRequiredOptIns = pipe(
  filter(propEq("required", true)),
  pluck("optInId"),
);

const Terms = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  const termsUri = useSelector(getProjectTermsUri);
  const acceptedOptIns = useSelector(getStepAcceptedOptIns(steps.TERMS));
  const declinedOptIns = useSelector(getStepDeclinedOptIns(steps.TERMS));
  const errors = useSelector(getStepErrors(steps.TERMS));
  const fields = useSelector(getStepFields(steps.TERMS));
  const loading = useSelector(isTermsStepLoading);
  const optIns = useSelector(getTermsOptIns);
  const fieldOptions = useSelector(getFieldOptions);

  const dispatch = useDispatch();
  const onPrevious = () =>
    dispatch({ type: SET_ACTIVE_STEP, step: steps.FILE_DETAILS });
  const onCancel = () =>
    dispatch({ type: SHOW_CANCEL_SUBMISSION_DIALOG, show: true });
  const onSubmit = () => dispatch({ type: SUBMIT });
  const onTermsAccepted = () => {
    setTermsOpen(false);
    dispatch({ type: SET_TERMS_ACCEPTED, accepted: true });
  };
  const onAcceptOptIn = id => dispatch({ type: ACCEPT_OPT_IN, id });
  const onViewOptInTerms = id => dispatch({ type: VIEW_OPT_IN_TERMS, id });
  const onDeclineOptIn = id => dispatch({ type: DECLINE_OPT_IN, id });

  const [termsOpen, setTermsOpen] = useState(false);

  const [submitActive, setSubmitActive] = useState(false);
  useEffect(() => {
    const requiredOptIns = getRequiredOptIns(optIns);

    if (!!optIns.length && !!requiredOptIns.length) {
      const requiredAccepted = isEmpty(
        difference(requiredOptIns, acceptedOptIns),
      );

      setSubmitActive(fields.termsAccepted && requiredAccepted);
    } else {
      setSubmitActive(fields.termsAccepted);
    }
  }, [fields.termsAccepted, optIns, acceptedOptIns]);

  const hideOtherConsiderations =
    fieldOptions?.terms_header_other_considerations &&
    fieldOptions?.terms_header_other_considerations?.hidden;

  return loading ? (
    <LoadingPanel />
  ) : (
    <Paper classes={{ root: classes.root }} elevation={0}>
      <form autoComplete="none">
        <div className={classes.sectionHeader}>
          <Typography variant="h2">
            {t("terms_heading")}{" "}
            <Typography variant="body2">{t("terms_subheading")}</Typography>
          </Typography>
        </div>

        <Button
          variant="contained"
          color="secondary"
          onClick={() => setTermsOpen(true)}
          error={t(firstError(errors, "termsAccepted"))}
        >
          {t("terms_view_terms")}
        </Button>
        {fields.termsAccepted && (
          <div className={classes.accepted}>
            <CheckIcon fontSize="small" fontWeight="bold" />
            {t("terms_accepted")}
          </div>
        )}

        {errors.termsAccepted && (
          <p>{t(firstError(errors, "termsAccepted"))}</p>
        )}

        {!!optIns.length && !hideOtherConsiderations && (
          <div className={classes.optInHeader}>
            <Typography variant="h2">
              {t("terms_other_considerations")}{" "}
              <Typography variant="body2">
                {t("terms_other_considerations_sub")}
              </Typography>
            </Typography>
          </div>
        )}

        {addIndex(map)(
          (optIn, index) => (
            <OptIn
              accepted={includes(optIn.optInId, acceptedOptIns)}
              declined={includes(optIn.optInId, declinedOptIns)}
              error={t(firstError(errors, `optIns.${optIn.optInId}`))}
              key={optIn.optInTitle}
              onAccept={() => onAcceptOptIn(optIn.optInId)}
              onDecline={() => onDeclineOptIn(optIn.optInId)}
              onViewTerms={() => onViewOptInTerms(optIn.optInId)}
              optIn={optIn}
            />
          ),
          optIns,
        )}

        <OptInTermsDialog />
        <TermsDialog
          visible={termsOpen}
          termsUri={termsUri}
          onAccept={onTermsAccepted}
          onDecline={() => setTermsOpen(false)}
        />
      </form>
      <div>
        <StepPanelButtonBar
          submitActive={submitActive}
          onSubmit={onSubmit}
          onCancel={onCancel}
          onPrevious={onPrevious}
        />
      </div>
    </Paper>
  );
};

export default Terms;
